import React from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  DollarSign,
  TrendingUp,
  Activity,
  Play,
  Pause,
  AlertTriangle,
  Trophy,
  Target,
  Calendar
} from 'lucide-react';
import api from '../../services/api';
import StatsCard from '../../components/dashboard/StatsCard';
import CurrentEventCard from '../../components/dashboard/CurrentEventCard';
import RecentTransactions from '../../components/dashboard/RecentTransactions';
import TopClients from '../../components/dashboard/TopClients';

const AdminOverview = () => {
  const navigate = useNavigate();

  const { data: dashboardData, isLoading, error } = useQuery(
    'admin-dashboard',
    () => api.get('/dashboard/admin').then(res => res.data),
    {
      refetchInterval: 30000,
      staleTime: 10000
    }
  );

  // Funciones de navegación para botones de acciones rápidas
  const handleNewEvent = () => {
    navigate('/admin/events/new');
  };

  const handleAddClient = () => {
    navigate('/admin/clients/new');
  };

  const handleUpdateResult = () => {
    navigate('/admin/betting');
  };

  const handleViewStats = () => {
    // Scroll to stats section in the same page
    const statsSection = document.querySelector('[data-stats-section]');
    if (statsSection) {
      statsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error al cargar dashboard
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {error.response?.data?.error || 'Error de conexión'}
        </p>
      </div>
    );
  }

  const { generalMetrics, activeEvent, recentTransactions, topClients } = dashboardData;

  const totalCommissions = Number(generalMetrics.total_commissions || 0);
  const totalGrossWinnings = Number(generalMetrics.total_gross_winnings || 0);
  const commissionRate = totalGrossWinnings > 0 ? (totalCommissions / totalGrossWinnings * 100) : 0;

  const statsCards = [
    {
      title: 'Clientes Activos',
      value: generalMetrics.active_clients,
      total: generalMetrics.total_clients,
      icon: Users,
      color: 'primary',
      trend: generalMetrics.trends?.clients?.display || '+0%',
      trendValue: generalMetrics.trends?.clients?.value || 0,
      description: 'vs mes anterior'
    },
    {
      title: 'Capital Gestionado',
      value: `$${Number(generalMetrics.total_capital_managed).toLocaleString()}`,
      icon: DollarSign,
      color: 'success',
      trend: generalMetrics.trends?.capital?.display || '+0%',
      trendValue: generalMetrics.trends?.capital?.value || 0,
      description: 'crecimiento mensual'
    },
    {
      title: 'Comisiones Retenidas',
      value: `$${totalCommissions.toLocaleString()}`,
      icon: Trophy,
      color: 'purple',
      trend: `${commissionRate.toFixed(1)}%`,
      trendValue: commissionRate,
      description: 'de ganancias brutas',
      highlight: true
    },
    {
      title: 'Ganancias Netas',
      value: `$${Number(generalMetrics.net_winnings || generalMetrics.total_winnings_generated).toLocaleString()}`,
      icon: TrendingUp,
      color: 'warning',
      trend: generalMetrics.trends?.profit?.display || '+0%',
      trendValue: generalMetrics.trends?.profit?.value || 0,
      description: 'descontando apuestas'
    },
    {
      title: 'ROI Promedio',
      value: generalMetrics.total_initial_capital > 0
        ? `${((generalMetrics.net_winnings || generalMetrics.total_winnings_generated) / generalMetrics.total_initial_capital * 100).toFixed(1)}%`
        : '0%',
      icon: Activity,
      color: 'primary',
      trend: generalMetrics.trends?.roi?.display || '+0%',
      trendValue: generalMetrics.trends?.roi?.value || 0,
      description: 'rendimiento neto'
    }
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-primary rounded-2xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              ¡Bienvenido de vuelta! 👋
            </h1>
            <p className="text-primary-100">
              Aquí tienes un resumen de la actividad de hoy
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold mb-1">
              ${Number(activeEvent?.currentBetTotal || 0).toLocaleString()}
            </p>
            <p className="text-primary-100 text-sm">
              Apuesta total actual
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatsCard {...stat} />
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CurrentEventCard activeEvent={activeEvent} />
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="card p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Acciones Rápidas
          </h3>

          <div className="space-y-3">
            <button
              onClick={handleNewEvent}
              className="w-full btn-primary hover:scale-105 transition-all duration-200 hover:shadow-lg"
              title="Crear un nuevo evento de apuestas"
            >
              <Play className="w-4 h-4 mr-2" />
              Nuevo Evento
            </button>

            <button
              onClick={handleAddClient}
              className="w-full btn-secondary hover:scale-105 transition-all duration-200 hover:shadow-lg"
              title="Registrar un nuevo cliente"
            >
              <Users className="w-4 h-4 mr-2" />
              Agregar Cliente
            </button>

            <button
              onClick={handleUpdateResult}
              className="w-full btn-secondary hover:scale-105 transition-all duration-200 hover:shadow-lg"
              title="Gestionar resultados de apuestas"
            >
              <Target className="w-4 h-4 mr-2" />
              Actualizar Resultado
            </button>

            <button
              onClick={handleViewStats}
              className="w-full btn-secondary hover:scale-105 transition-all duration-200 hover:shadow-lg"
              title="Ver gráficos y estadísticas detalladas"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Ver Estadísticas
            </button>
          </div>

          {activeEvent && (
            <div className="mt-6 p-4 bg-success-50 dark:bg-success-900/20 rounded-lg border border-success-200 dark:border-success-800">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-success-700 dark:text-success-300">
                  Evento Activo
                </span>
              </div>
              <p className="text-xs text-success-600 dark:text-success-400">
                {activeEvent.name} - Paso {activeEvent.current_step}/13
              </p>
              <p className="text-xs text-success-600 dark:text-success-400 mt-1">
                {activeEvent.participant_count} participantes
              </p>
            </div>
          )}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        data-stats-section
      >
        <TopClients clients={topClients} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <RecentTransactions transactions={recentTransactions} />
      </motion.div>

      {activeEvent?.participantDetails?.some(p => !p.canBet) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card p-6 border-l-4 border-warning-500"
        >
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-warning-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                ⚠️ Alerta de Riesgo
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                Algunos clientes no tienen saldo suficiente para el paso actual ({activeEvent.current_step}):
              </p>
              <div className="space-y-2">
                {activeEvent.participantDetails
                  .filter(p => !p.canBet)
                  .map(participant => (
                    <div key={participant.userId} className="py-2 px-3 bg-warning-50 dark:bg-warning-900/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-warning-800 dark:text-warning-200">
                          {participant.fullName}
                        </span>
                        <span className="text-xs text-warning-600 dark:text-warning-400">
                          Faltante: ${participant.shortfall?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1 text-xs text-warning-600 dark:text-warning-400">
                        <span>Saldo: ${Number(participant.currentBalance).toLocaleString()}</span>
                        <span>Requiere: ${Number(participant.betAmount).toLocaleString()}</span>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminOverview;